import axios from "axios";
import memoryCache from "./cache";
import type { OfferCampaignRecord } from "@/app/utils/offerPage";
import { getLandingPageApiUrl } from "@/app/utils/landingPageApi";
import { BRAND_NAMES } from "@/app/utils/utils";

const campaignsCacheKey = "campaigns_with_content";

const proxyNoCacheConfig = {
	headers: {
		"Cache-Control": "no-cache",
		Pragma: "no-cache",
	},
};

function getApiProxyUrl(requestOrigin?: string): string {
	if (typeof window !== "undefined") {
		return `${window.location.origin}/api/proxy`;
	}

	if (requestOrigin) {
		return `${requestOrigin}/api/proxy`;
	}

	const serverBaseUrl =
		process.env.INTERNAL_BASE_URL ||
		process.env.NEXT_PUBLIC_BASE_URL ||
		"http://localhost:3000";

	return `${serverBaseUrl}/api/proxy`;
}

async function fetchProxyData<T>(
	endpoint: string,
	requestOrigin?: string
): Promise<T> {
	const response = await axios.get<T>(getApiProxyUrl(requestOrigin), {
		...proxyNoCacheConfig,
		params: {
			endpoint,
			_: Date.now(),
		},
		validateStatus: (status) => status >= 200 && status < 300,
	});

	return response.data;
}

async function fetchCmsRawRecords(requestOrigin?: string): Promise<
	Record<string, unknown>[]
> {
	if (typeof window === "undefined") {
		const apiKey = process.env.API_KEY;

		if (apiKey) {
			try {
				const landingPageApiUrl = getLandingPageApiUrl();
				const response = await axios.get(
					`${landingPageApiUrl}/api/get-data/campaign/cms?brand=8&sub_brand=${BRAND_NAMES.SUB_BRAND}`,
					{
						headers: { "x-api-key": apiKey },
						validateStatus: (status) => status >= 200 && status < 300,
					}
				);

				if (Array.isArray(response.data)) {
					return response.data as Record<string, unknown>[];
				}

				console.error(
					"[fetchCMSData] Resposta inesperada do backend CMS:",
					response.data
				);
			} catch (error) {
				console.error(
					"[fetchCMSData] Falha ao buscar CMS no backend, tentando proxy:",
					error
				);
			}
		}
	}

	try {
		const data = await fetchProxyData<unknown>("cms", requestOrigin);

		if (!Array.isArray(data)) {
			console.error("[fetchCMSData] Resposta inesperada do proxy CMS:", data);
			return [];
		}

		return data as Record<string, unknown>[];
	} catch (error) {
		console.error("[fetchCMSData] Falha no proxy CMS:", error);
		return [];
	}
}

const EMPTY_CMS_DATA = {
	ids: [],
	bannerImages: [],
	bannerImagesTablet: [],
	bannerImagesMobile: [],
	calls: [],
	links: [],
	types: [],
	actives: [],
	contents: [],
	title_contents: [],
	start_datetimes: [],
	end_datetimes: [],
	orders: [],
	defenseTypes: [],
	vehicle_names: [],
	campaign_json: [],
};

function toCachedCmsData(reduced: ReturnType<typeof reduceCmsRecords>) {
	return {
		ids: reduced.ids,
		bannerImages: reduced.bannerImages,
		bannerImagesTablet: reduced.bannerImagesTablet,
		bannerImagesMobile: reduced.bannerImagesMobile,
		calls: reduced.calls,
		links: reduced.links,
		types: reduced.types,
		actives: reduced.actives,
		contents: reduced.contents,
		title_contents: reduced.title_contents,
		start_datetimes: reduced.start_datetimes,
		end_datetimes: reduced.end_datetimes,
		orders: reduced.orders,
		defenseTypes: reduced.defenseTypes,
		vehicle_names: reduced.vehicle_names,
		campaign_json: reduced.campaign_json,
	};
}

function reduceCmsRecords(data: Record<string, unknown>[]) {
	return data.reduce(
		(acc: {
			ids: number[];
			bannerImages: string[];
			bannerImagesTablet: string[];
			bannerImagesMobile: string[];
			calls: string[];
			links: string[];
			types: string[];
			actives: boolean[];
			contents: string[];
			title_contents: string[];
			start_datetimes: string[];
			end_datetimes: string[];
			orders: number[];
			defenseTypes: string[];
			vehicle_names: string[];
			campaign_json: string[];
		}, element) => {
			acc.ids.push(Number(element.id));
			acc.bannerImages.push(String(element.banner_desktop || ""));
			acc.bannerImagesTablet.push(String(element.banner_tablet || ""));
			acc.bannerImagesMobile.push(String(element.banner_mobile || ""));
			acc.calls.push(String(element.call || ""));
			acc.links.push(String(element.link || ""));
			acc.types.push(String(element.type || ""));
			acc.actives.push(Boolean(element.active));
			acc.contents.push(String(element.content || ""));
			acc.title_contents.push(String(element.title_content || ""));
			acc.start_datetimes.push(String(element.start_datetime || ""));
			acc.end_datetimes.push(String(element.end_datetime || ""));
			acc.orders.push(Number(element.order ?? 0));
			acc.defenseTypes.push(String(element.defenseType || ""));
			acc.vehicle_names.push(String(element.vehicle_name || ""));
			acc.campaign_json.push(
				String(element.campaign_json || element.json || "")
			);
			return acc;
		},
		{
			ids: [],
			bannerImages: [],
			bannerImagesTablet: [],
			bannerImagesMobile: [],
			calls: [],
			links: [],
			types: [],
			actives: [],
			contents: [],
			title_contents: [],
			start_datetimes: [],
			end_datetimes: [],
			orders: [],
			defenseTypes: [],
			vehicle_names: [],
			campaign_json: [],
		}
	);
}

export const fetchOfferCampaigns = async (
	requestOrigin?: string
): Promise<OfferCampaignRecord[]> => {
	const data = await fetchCmsRawRecords(requestOrigin);

	return data.map(normalizeOfferCampaign);
};

function normalizeOptionalString(value: unknown): string {
	if (value === null || value === undefined) return "";

	const normalized = String(value).trim();
	if (normalized === "null" || normalized === "undefined") return "";

	return normalized;
}

function normalizeOfferCampaign(raw: Record<string, unknown>): OfferCampaignRecord {
	const campaignJson =
		raw.campaign_json ?? raw.campaignJson ?? raw.json ?? "";

	return {
		id: Number(raw.id),
		type: normalizeOptionalString(raw.type),
		active: raw.active as boolean | number | string,
		call: normalizeOptionalString(raw.call),
		vehicle_name: normalizeOptionalString(
			raw.vehicle_name ?? raw.vehicleName
		),
		campaign_json: campaignJson as string | Record<string, unknown>,
		json: raw.json as string | Record<string, unknown> | undefined,
		start_datetime: String(raw.start_datetime || raw.startDate || ""),
		end_datetime: raw.end_datetime
			? String(raw.end_datetime)
			: raw.endDate
				? String(raw.endDate)
				: null,
		order: Number(raw.order ?? raw.id_order ?? 0),
	};
};

export const fetchCampaignsWithContent = async () => {
  let cachedCampaigns = memoryCache.get<{
    contents: string[];
    title_contents: string[];
    calls: string[];
  }>(campaignsCacheKey);

  if (!cachedCampaigns) {
    const data = await fetchProxyData<unknown>("campaigns");
    const records = Array.isArray(data) ? data : [];

    const { contents, title_contents, calls } = records.reduce(
      (acc: any, element: any) => {
        acc.contents.push(element.content);
        acc.title_contents.push(element.title_content);
        acc.calls.push(element.call || "");
        return acc;
      },
      {
        contents: [],
        title_contents: [],
        calls: [],
      }
    );

    cachedCampaigns = {
      contents,
      title_contents,
      calls,
    };

    memoryCache.set(campaignsCacheKey, cachedCampaigns);
  }

  return cachedCampaigns;
};
const bannersCacheKey = "banners_homepage";
const cmsCacheKey = "cms_dashboard";

export const fetchCMSData = async (
	revalidate?: boolean,
	requestOrigin?: string
) => {
  let cachedCMSData = memoryCache.get<{
    ids: number[];
    bannerImages: any[];
    bannerImagesTablet: any[];
    bannerImagesMobile: any[];
    calls: string[];
    links: any[];
    types: string[];
    actives: boolean[];
    contents: any[];
    title_contents: string[];
    start_datetimes: any[];
    end_datetimes: any[];
    orders: number[];
    defenseTypes: any[];
    vehicle_names: string[];
    campaign_json: string[];
  }>(cmsCacheKey);

  if (!cachedCMSData || revalidate) {
    try {
      const rawRecords = await fetchCmsRawRecords(requestOrigin);
      cachedCMSData = toCachedCmsData(reduceCmsRecords(rawRecords));
      memoryCache.set(cmsCacheKey, cachedCMSData);
    } catch (error) {
      console.error("[fetchCMSData] Falha ao carregar dados do CMS:", {
        requestOrigin,
        hasApiKey: Boolean(process.env.API_KEY),
        landingPageApiUrl: process.env.LANDING_PAGE_API_URL,
        error,
      });

      if (!cachedCMSData) {
        cachedCMSData = { ...EMPTY_CMS_DATA };
      }
    }
  }

  return cachedCMSData;
};

export const fetchBanners = async () => {
  let cachedBanners = memoryCache.get<{
    ids: number[];
    contents: string[];
    title_contents: string[];
    defenseTypes: string[];
    bannerImages: string[];
    bannerImagesTablet: string[];
    bannerImagesMobile: string[];
    calls: string[];
    links: string[];
    types: string[];
  }>(bannersCacheKey);

  if (!cachedBanners) {
    const data = await fetchProxyData<unknown>("banners");
    const records = Array.isArray(data) ? data : [];

    const {
      ids,
      contents,
      defenseTypes,
      bannerImages,
      bannerImagesTablet,
      bannerImagesMobile,
      calls,
      links,
      title_contents,
      types,
    } = records.reduce(
      (acc: any, element: any) => {
        acc.ids.push(element.id);
        acc.contents.push(element.content);
        acc.defenseTypes.push(element.defenseType);
        acc.bannerImages.push(element.banner_desktop);
        acc.bannerImagesTablet.push(element.banner_tablet);
        acc.bannerImagesMobile.push(element.banner_mobile);
        acc.calls.push(element.call);
        acc.links.push(element.link);
        acc.title_contents.push(element.title_content);
        acc.types.push(element.type);

        return acc;
      },
      {
        ids: [],
        contents: [],
        defenseTypes: [],
        bannerImages: [],
        bannerImagesTablet: [],
        bannerImagesMobile: [],
        calls: [],
        links: [],
        title_contents: [],
        types: [],
      }
    );

    cachedBanners = {
      ids,
      contents,
      defenseTypes,
      bannerImages,
      bannerImagesTablet,
      bannerImagesMobile,
      calls,
      links,
      title_contents,
      types,
    };

    memoryCache.set(bannersCacheKey, cachedBanners);
  }

  return cachedBanners;
};
